import React, { useEffect, useMemo } from "react";
import Grid from "@mui/material/Grid";
import ColumnGroupingTable from "../../../table/ColumnGroupingTable.component";

const calculateEarningsAtRisk = (data, columns) => {
  const niiChangeRow = data.find(
    (row) => row.type === "Change in NII caused by re-pricing",
  );

  if (!niiChangeRow) return 0;

  const monthColumns = columns.slice(2).map((col) => col.id);

  const sum = monthColumns.reduce((acc, monthId) => {
    const value = niiChangeRow[monthId];
    return acc + (typeof value === "number" ? value : 0);
  }, 0);

  return sum;
};

const NiiResultTable = ({
  niiOutputTablecolumn,
  niiOutputTableData,
  setNiiResultTableColumn,
  setNiiResultTableData,
  selectedDecimalsIn,
  selectedFigureDivisor,
  selectedMonthEnd,
  latestAnnualNIIForecastAmount,
} = {}) => {
  const columns = useMemo(() => {
    return (
      [
        {
          label: `NII100bps Results for: ${selectedMonthEnd?.format("MM/DD/YYYY")}`,
          children: [
            {
              id: "name",
              label: "Name",
              minWidth: 140,
            },
            {
              id: "value",
              label: "Value",
              minWidth: 140,
              align: "right",
            },
          ],
        },
      ] || []
    );
  }, [selectedMonthEnd]);

  const datas = useMemo(() => {
    if (
      !niiOutputTableData ||
      niiOutputTableData.length === 0 ||
      !niiOutputTablecolumn ||
      niiOutputTablecolumn.length === 0
    ) {
      return [];
    }

    const earningsAtRisk = calculateEarningsAtRisk(
      niiOutputTableData,
      niiOutputTablecolumn,
    );
    const formattedEarningsAtRisk = parseFloat(earningsAtRisk.toFixed(2));

    const divisor = Number(selectedFigureDivisor) || 1;

    const niiForecast =
      parseFloat(
        (latestAnnualNIIForecastAmount / divisor)?.toFixed(selectedDecimalsIn),
      ) || 0;

    let parallelShiftPercentage = 0;

    if (niiForecast !== 0) {
      parallelShiftPercentage = (formattedEarningsAtRisk / niiForecast) * 100;
    }

    const formattedParallelShift = `${parallelShiftPercentage.toFixed(2)}%`;

    return [
      {
        name: "12 months Earnings at Risk",
        value: formattedEarningsAtRisk,
        cellStyles: { fontWeight: "bold" },
        dynamicCellStyles: {
          value: {
            color: parseFloat(formattedEarningsAtRisk) < 0 ? "red" : "black",
          },
        },
      },
      {
        name: "Latest Annual NII Forecast",
        value: niiForecast,
        cellStyles: { fontWeight: "bold" },
        dynamicCellStyles: {
          value: {
            color: niiForecast < 0 ? "red" : "black",
          },
        },
      },
      {
        name: "100bp Parallel Shift (% Profit)",
        value: formattedParallelShift,
        cellStyles: { fontWeight: "bold" },
        dynamicCellStyles: {
          value: {
            color: parallelShiftPercentage < 0 ? "red" : "black",
          },
        },
      },
    ];
  }, [
    niiOutputTableData,
    niiOutputTablecolumn,
    selectedFigureDivisor,
    latestAnnualNIIForecastAmount,
    selectedDecimalsIn,
  ]);

  useEffect(() => {
    if (datas) {
      setNiiResultTableColumn(columns);
      setNiiResultTableData(datas);
    }
  }, [columns, datas, setNiiResultTableColumn, setNiiResultTableData]);

  return (
    <Grid
      container
      spacing={2}
      sx={{
        marginBottom: 0,
        width: "50%",
        margin: "0 auto",
        border: "1px solid #e0e0e0",
        height: "152px",
      }}
    >
      <Grid size={{ xs: 12, md: 12, lg: 12 }}>
        <ColumnGroupingTable
          columns={columns}
          datas={datas}
          loading={false}
          disablePagination={true}
          applyThousandsSeparator={true}
          tableHeight={185}
          hideChildRowHeader
        />
      </Grid>
    </Grid>
  );
};

export default NiiResultTable;
